import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { quoteNotesApi } from "@/lib/quote-notes";
import type { QuoteDetailDto } from "@/lib/quotes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";

export interface QuoteCommentsCardProps {
  quote: QuoteDetailDto;
}

/**
 * Inline Comments card on the Quote tab, backed by the same QuoteExtraNotes
 * row as the Special Notes tab's Extra Notes card (which has been removed to
 * avoid duplication). Both surfaces share the React Query cache key so edits
 * propagate without reload.
 *
 * Always-on Textarea + Save pattern mirrors EntitySpecialNotesTab.
 * Query key: ["quote", quoteId, "extra-notes"]
 */
export function QuoteCommentsCard({ quote }: QuoteCommentsCardProps) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");

  const notesQ = useQuery({
    queryKey: ["quote", quote.quoteId, "extra-notes"],
    queryFn: () => quoteNotesApi.getExtraNotes(quote.quoteId),
  });

  // Sync draft when data loads or changes externally (not mid-edit — we
  // detect external change via notesQ.data reference, same approach as
  // EntitySpecialNotesTab).
  useEffect(() => {
    setDraft(notesQ.data?.extraNote ?? "");
  }, [notesQ.data?.extraNote]);

  const save = useMutation({
    mutationFn: () =>
      quoteNotesApi.putExtraNotes(quote.quoteId, {
        note: draft,
        rowVersionBase64: notesQ.data?.rowVersionBase64 ?? "",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId, "extra-notes"] });
      toast.success("Comments saved.");
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  const isDirty = draft !== (notesQ.data?.extraNote ?? "");

  return (
    <Card className="border border-slate-400 rounded-xl overflow-hidden bg-white shadow-sm flex-1 flex flex-col">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
        <CardTitle role="heading" aria-level={3} className="text-xs font-bold text-slate-800 uppercase tracking-wider">Comments</CardTitle>
      </CardHeader>
      <CardContent className="p-4 flex flex-col flex-1 space-y-4">
        <Label htmlFor="comments" className="sr-only">Comments</Label>
        <Textarea
          id="comments"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Internal comments…"
          className="flex-1 resize-none min-h-[120px]"
        />
        <div className="flex justify-end pt-4 mt-auto">
          <Button
            variant="outline"
            className="rounded-full border-transparent bg-info/15 text-primary hover:bg-info/25 font-semibold"
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending || notesQ.isLoading || !isDirty}
          >
            <Save className="w-4 h-4 mr-2" />
            {save.isPending ? "Saving…" : "Save Comments"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
